import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactSelect from "react-select";
import {
  BACKEND_SEARCH_DEBOUNCE_MS,
  BACKEND_SEARCH_LIMIT,
  fetchOptionsFromApi,
  sortOptionsAlphabetically,
} from "./filterHelper";
import styles from "./BackendSearchSelect.module.css";

function getClientId() {
  try {
    return JSON.parse(localStorage.getItem("clientId"));
  } catch {
    return null;
  }
}

function toOptionList(value) {
  if (Array.isArray(value) && value.length > 0) {
    return value.map((input) =>
      typeof input === "object" && input !== null
        ? input
        : { value: input, label: input }
    );
  }
  if (value && !Array.isArray(value)) {
    return typeof value === "object"
      ? [value]
      : [{ value, label: value }];
  }
  return [];
}

/**
 * Reusable ReactSelect that searches via API when field.backendSearch is true.
 * Selected values stay in the options list even if the latest search omits them.
 */
export default function BackendSearchSelect({
  field,
  value,
  isMulti = false,
  onChange,
  placeholder = "Select...",
  menuPlacement = "top",
  styles: selectStyles,
  className = "basic-single",
  classNamePrefix = "Select Operator",
  components,
}) {
  const selectedOptions = useMemo(() => toOptionList(value), [value]);
  const [remoteOptions, setRemoteOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [hasOpened, setHasOpened] = useState(false);

  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);

  const apiKey = field?.apiKey;

  const fetchOptions = useCallback(
    async (searchTerm = "") => {
      const clientId = getClientId();
      if (!clientId || !apiKey) return;

      const normalizedTerm = String(searchTerm ?? "").trim();
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setIsLoading(true);

      try {
        const data = await fetchOptionsFromApi(apiKey, clientId, {
          backendSearch: true,
          searchTerm: normalizedTerm,
          limit: BACKEND_SEARCH_LIMIT,
        });
        if (requestIdRef.current !== requestId) return;
        setRemoteOptions(Array.isArray(data) ? data : []);
      } catch (error) {
        if (requestIdRef.current === requestId) {
          console.error(error);
          setRemoteOptions([]);
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    [apiKey]
  );

  const scheduleFetch = useCallback(
    (searchTerm) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchOptions(searchTerm);
      }, BACKEND_SEARCH_DEBOUNCE_MS);
    },
    [fetchOptions]
  );

  const cancelScheduledFetch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Reset remote cache when switching to a different API-backed field.
  useEffect(() => {
    setRemoteOptions([]);
    setHasOpened(false);
    setInputValue("");
  }, [apiKey]);

  const mergedOptions = useMemo(() => {
    const byValue = new Map();
    selectedOptions.forEach((option) => {
      if (option?.value == null) return;
      byValue.set(String(option.value), option);
    });
    remoteOptions.forEach((option) => {
      if (option?.value == null) return;
      const key = String(option.value);
      const existing = byValue.get(key);
      if (!existing) {
        byValue.set(key, option);
        return;
      }
      // Keep selection, enrich with status from API when available.
      if (!existing.status && option.status) {
        byValue.set(key, { ...existing, ...option, value: existing.value, label: existing.label || option.label });
      }
    });
    return sortOptionsAlphabetically(Array.from(byValue.values()));
  }, [remoteOptions, selectedOptions]);

  const mergedStyles = useMemo(() => {
    const withParent = (key, extras) => (provided, state) => ({
      ...provided,
      ...(typeof selectStyles?.[key] === "function"
        ? selectStyles[key](provided, state)
        : {}),
      ...extras,
    });

    return {
      ...selectStyles,
      container: withParent("container", {
        width: "100%",
        maxWidth: "100%",
      }),
      control: withParent("control", {
        fontSize: "12px",
        maxWidth: "100%",
        overflow: "hidden",
        boxShadow: "none",
        outline: "none",
        "&:hover": {
          boxShadow: "none",
        },
      }),
      valueContainer: withParent("valueContainer", {
        overflow: "hidden",
        maxWidth: "100%",
        minWidth: 0,
      }),
      input: withParent("input", {
        fontSize: "12px",
        margin: 0,
        // Keep caret usable: don't clip the live input node.
        maxWidth: "100%",
        outline: "none",
        boxShadow: "none",
      }),
      placeholder: withParent("placeholder", { fontSize: "12px" }),
      singleValue: withParent("singleValue", { fontSize: "12px" }),
      option: withParent("option", {
        fontSize: "12px",
        lineHeight: "1.4",
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        overflow: "visible",
        textOverflow: "unset",
      }),
      multiValueLabel: withParent("multiValueLabel", { fontSize: "12px" }),
      menu: withParent("menu", { fontSize: "12px" }),
      menuPortal: (provided, state) => ({
        ...provided,
        ...(typeof selectStyles?.menuPortal === "function"
          ? selectStyles.menuPortal(provided, state)
          : {}),
        zIndex: 9999,
      }),
      noOptionsMessage: withParent("noOptionsMessage", { fontSize: "12px" }),
      loadingMessage: withParent("loadingMessage", { fontSize: "12px" }),
    };
  }, [selectStyles]);

  const handleInputChange = useCallback(
    (nextValue, meta) => {
      const action = meta?.action;

      // User typing — keep caret at end by only updating on real input changes.
      if (action === "input-change") {
        const value = nextValue ?? "";
        setInputValue(value);
        scheduleFetch(value);
        return value;
      }

      // After selecting an option, always clear search text.
      // Stale text + caret at index 0 makes Backspace a no-op (can't delete
      // selected chips/values until the input is empty).
      if (action === "set-value" || action === "menu-close") {
        cancelScheduledFetch();
        setInputValue("");
        return "";
      }

      // Ignore blur/reset actions that fight controlled inputValue / caret.
      if (action === "input-blur" || action === "menu-open") {
        return inputValue;
      }

      return inputValue;
    },
    [cancelScheduledFetch, inputValue, scheduleFetch]
  );

  return (
    <div className={styles.wrap}>
      <ReactSelect
        isMulti={isMulti}
        className={className}
        classNamePrefix={classNamePrefix}
        value={selectedOptions}
        isSearchable
        isClearable={!isMulti}
        backspaceRemovesValue
        isLoading={isLoading}
        filterOption={() => true}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onMenuOpen={() => {
          setHasOpened(true);
          fetchOptions(inputValue);
        }}
        components={components}
        name="userInput"
        options={mergedOptions}
        placeholder={
          isLoading && !hasOpened
            ? "Loading..."
            : isLoading
              ? "Searching..."
              : placeholder
        }
        noOptionsMessage={() =>
          isLoading ? "Searching..." : "No options found"
        }
        menuPlacement={menuPlacement}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        onChange={onChange}
        styles={mergedStyles}
      />
    </div>
  );
}
